import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { ArticleCard } from "@/components/site/ArticleCard";
import { listCategories, listPublishedArticles } from "@/lib/articles.functions";
import { SITE } from "@/lib/site";
import { GUIDES, getGuide } from "@/lib/guides";

export const Route = createFileRoute("/guide/$slug")({
  loader: async ({ params, context }) => {
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    if (guide.relatedCategorySlug) {
      await Promise.all([
        context.queryClient.prefetchQuery({
          queryKey: ["category", guide.relatedCategorySlug],
          queryFn: () =>
            listPublishedArticles({
              data: { categorySlug: guide.relatedCategorySlug, limit: 3 },
            }),
        }),
        context.queryClient.prefetchQuery({
          queryKey: ["categories"],
          queryFn: () => listCategories(),
        }),
      ]);
    }
    return { guide };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { guide } = loaderData;
    const url = `${SITE.url}/guide/${params.slug}`;
    return {
      meta: [
        { title: `${guide.metaTitle} | ${SITE.name}` },
        { name: "description", content: guide.metaDescription },
        { property: "og:title", content: guide.metaTitle },
        { property: "og:description", content: guide.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:title", content: guide.metaTitle },
        { name: "twitter:description", content: guide.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.metaDescription,
            inLanguage: "en",
            author: { "@type": "Organization", name: SITE.name },
            publisher: {
              "@type": "Organization",
              name: SITE.name,
              logo: { "@type": "ImageObject", url: `${SITE.url}/favicon.ico` },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            about: ["Local SEO", "Google Business Profile", "Google Maps", "Map Pack"],
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Local SEO agencies, GBP specialists, and local business owners",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE.url}/guide` },
              { "@type": "ListItem", position: 3, name: guide.title, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-24 text-center">
        <h1 className="headline-display text-4xl">Guide not found</h1>
        <p className="mt-4 text-muted-foreground">
          <Link to="/guide" className="text-accent hover:underline">View all guides →</Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  ),
  component: GuidePage,
});

function GuideBody() {
  const params = Route.useParams();
  const guide = getGuide(params.slug)!;
  const tocId = (h: string) => h.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <article className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li><Link to="/guide" className="hover:text-accent">Guides</Link></li>
          <li aria-hidden>/</li>
          <li className="truncate max-w-[200px]">{guide.eyebrow}</li>
        </ol>
      </nav>
      <header className="mt-6">
        <p className="eyebrow">{guide.eyebrow}</p>
        <h1 className="headline-display mt-3 text-4xl sm:text-5xl">{guide.title}</h1>
        <p className="mt-5 text-lg text-muted-foreground leading-snug">{guide.summary}</p>
      </header>

      {guide.sections.length > 2 && (
        <aside className="mt-10 border border-border bg-paper-soft p-5">
          <p className="eyebrow">On this page</p>
          <ol className="mt-3 space-y-1 text-sm">
            {guide.sections.map((s) => (
              <li key={s.heading}>
                <a href={`#${tocId(s.heading)}`} className="text-foreground/80 hover:text-accent">
                  {s.heading}
                </a>
              </li>
            ))}
            <li>
              <a href="#faq" className="text-foreground/80 hover:text-accent">FAQ</a>
            </li>
          </ol>
        </aside>
      )}

      <div className="prose-article mt-10">
        {guide.sections.map((s) => (
          <section key={s.heading} id={tocId(s.heading)} className="scroll-mt-24">
            <h2>{s.heading}</h2>
            {s.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>
        ))}
      </div>

      <section id="faq" className="mt-16 scroll-mt-24">
        <div className="border-b border-foreground pb-3 mb-6">
          <h2 className="headline-display text-2xl">Frequently asked questions</h2>
        </div>
        <dl className="space-y-6">
          {guide.faq.map((f) => (
            <div key={f.question}>
              <dt className="font-semibold text-base">{f.question}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <NewsletterCTA />

      {guide.relatedCategorySlug && <RelatedFromCategory slug={guide.relatedCategorySlug} />}

      <section className="mt-16">
        <div className="border-b border-foreground pb-3 mb-6">
          <h2 className="headline-display text-2xl">More guides</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {GUIDES.filter((g) => g.slug !== guide.slug).map((g) => (
            <Link
              key={g.slug}
              to="/guide/$slug"
              params={{ slug: g.slug }}
              className="block border border-border p-4 hover:border-foreground transition-colors"
            >
              <p className="eyebrow">{g.eyebrow}</p>
              <p className="mt-1 font-semibold">{g.title}</p>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}

function RelatedFromCategory({ slug }: { slug: string }) {
  const { data } = useSuspenseQuery({
    queryKey: ["category", slug],
    queryFn: () => listPublishedArticles({ data: { categorySlug: slug, limit: 3 } }),
  });
  const { data: categories } = useSuspenseQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
  });
  if (data.articles.length === 0) return null;
  const catMap = new Map(categories.map((c) => [c.id, { slug: c.slug, name: c.name }] as const));
  return (
    <section className="mt-16">
      <div className="border-b border-foreground pb-3 mb-6">
        <h2 className="headline-display text-2xl">Latest from this beat</h2>
      </div>
      <div className="grid gap-8 sm:grid-cols-3">
        {data.articles.map((a) => (
          <ArticleCard key={a.id} article={a} categories={catMap} variant="compact" />
        ))}
      </div>
    </section>
  );
}

function GuidePage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <GuideBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}
